import {useRef, useState} from 'react';

import * as api from '../api';
import {ApiError} from '../api';
import {Download, Forward} from '../components/Icons';
import {VideoPlayer} from '../components/VideoPlayer';
import {arNum, ASPECT_CSS, ASPECT_DIMENSIONS, fileSize, formatWhen} from '../format';
import {useStudio, assetUrl} from '../store';
import type {Aspect, RenderSettings} from '../types';

const ASPECTS: Aspect[] = ['9:16', '16:9', '1:1'];

function PublishButton({projectId, userId}: {projectId: string; userId: string}) {
  const [phase, setPhase] = useState<'idle' | 'generating' | 'review' | 'publishing' | 'done' | 'error'>('idle');
  const [caption, setCaption] = useState('');
  const [postUrl, setPostUrl] = useState('');
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setPhase('generating');
    setError('');
    try {
      const data = await api.generateCaption(userId, projectId);
      setCaption(data.full_text);
      setPhase('review');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'فشل توليد التعليق');
      setPhase('error');
    }
  };

  const handlePublish = async () => {
    setPhase('publishing');
    try {
      const result = await api.publishToSocial(userId, projectId);
      setPostUrl(result.post_url);
      setPhase('done');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'فشل النشر');
      setPhase('error');
    }
  };

  if (phase === 'done') {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 12, background: 'var(--band)', borderRadius: 8, width: '100%'}}>
        <span style={{font: '600 14px/1.5 var(--sans)', color: 'var(--green, #2a7)'}}>✓ تم النشر بنجاح</span>
        <p className="empty-note" style={{margin: 0}}>قد يحتاج الفيديو 1-3 دقائق ليظهر على فيسبوك</p>
        {postUrl && (
          <a href={postUrl} target="_blank" rel="noopener" className="btn sm">
            شاهد المنشور ↗
          </a>
        )}
      </div>
    );
  }

  if (phase === 'review') {
    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--band)', borderRadius: 8, width: '100%'}}>
        <span style={{font: '600 13px/1.5 var(--sans)', color: 'var(--ink)'}}>تعليق المنشور</span>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={5}
          dir="rtl"
          style={{font: '400 14px/1.8 var(--sans)', padding: 10, borderRadius: 6, border: '1px solid var(--rule)', resize: 'vertical'}}
        />
        <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
          <button type="button" className="btn sm" onClick={() => void handlePublish()}>
            📤 انشر الآن
          </button>
          <button type="button" className="btn-out sm" onClick={() => setPhase('idle')}>
            إلغاء
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6}}>
        <span className="err-note">{error}</span>
        <button type="button" className="btn-out sm" onClick={() => setPhase('idle')}>حاول مرة أخرى</button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="btn-out lg"
      disabled={phase === 'generating' || phase === 'publishing'}
      onClick={() => void handleGenerate()}
    >
      {phase === 'generating' ? '⏳ جاري توليد التعليق…' : phase === 'publishing' ? '⏳ جاري النشر…' : '📤 انشر على السوشال ميديا'}
    </button>
  );
}

const DEFAULT_SETTINGS: RenderSettings = {
  text_position: 'bottom',
  font_size: 'large',
  text_color: 'white',
  text_background: 'none',
  logo_s3_key: '',
  logo_position: 'top-right',
};

const TEXT_POSITIONS = [
  {value: 'top', label: 'أعلى'},
  {value: 'center', label: 'وسط'},
  {value: 'bottom', label: 'أسفل'},
] as const;

const FONT_SIZES = [
  {value: 'small', label: 'صغير'},
  {value: 'medium', label: 'متوسط'},
  {value: 'large', label: 'كبير'},
] as const;

const TEXT_COLORS = [
  {value: 'white', label: 'أبيض', css: '#fff'},
  {value: 'black', label: 'أسود', css: '#000'},
  {value: 'gold', label: 'ذهبي', css: '#ffd700'},
] as const;

const TEXT_BACKGROUNDS = [
  {value: 'none', label: 'بدون'},
  {value: 'bar', label: 'شريط شفاف'},
] as const;

const LOGO_POSITIONS = [
  {value: 'top-right', label: '↗ أعلى يمين'},
  {value: 'top-left', label: '↖ أعلى يسار'},
  {value: 'bottom-right', label: '↘ أسفل يمين'},
  {value: 'bottom-left', label: '↙ أسفل يسار'},
] as const;

function SegControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly {value: T; label: string}[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" style={{background: 'var(--paper-raised)', flexWrap: 'wrap'}}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={value === opt.value ? 'on' : ''}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function CustomizationPanel({onReRender}: {onReRender?: () => void}) {
  const s = useStudio();
  const project = s.project!;
  const scenes = s.scenes();
  const userId = s.user!.id;

  const saved = project.document.render_settings;
  const [settings, setSettings] = useState<RenderSettings>({...DEFAULT_SETTINGS, ...saved});
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const firstScene = scenes[0];
  const previewText = firstScene?.on_screen_copy || firstScene?.script_text || 'نص تجريبي';
  const bgUrl = firstScene?.visual ? assetUrl(firstScene.visual) : '';

  const update = <K extends keyof RenderSettings>(key: K, value: RenderSettings[K]) => {
    setSettings((prev) => ({...prev, [key]: value}));
  };

  // CSS preview positioning
  const positionStyle: React.CSSProperties = {
    position: 'absolute',
    left: '10%',
    right: '10%',
    textAlign: 'center',
    ...(settings.text_position === 'top' && {top: '10%'}),
    ...(settings.text_position === 'center' && {top: '50%', transform: 'translateY(-50%)'}),
    ...(settings.text_position === 'bottom' && {bottom: '12%'}),
  };

  const fontSizeMap = {small: 11, medium: 14, large: 17};
  const colorMap = {white: '#fff', black: '#000', gold: '#ffd700'};

  const textStyle: React.CSSProperties = {
    fontSize: fontSizeMap[settings.font_size],
    color: colorMap[settings.text_color],
    textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
    fontFamily: 'var(--naskh)',
    fontWeight: 700,
    direction: 'rtl',
    padding: settings.text_background === 'bar' ? '8px 12px' : undefined,
    background: settings.text_background === 'bar' ? 'rgba(0,0,0,0.55)' : undefined,
    borderRadius: settings.text_background === 'bar' ? 4 : undefined,
  };

  const logoStyle: React.CSSProperties = {
    position: 'absolute',
    width: '15%',
    height: 'auto',
    ...(settings.logo_position.includes('top') ? {top: '4%'} : {bottom: '4%'}),
    ...(settings.logo_position.includes('right') ? {right: '4%'} : {left: '4%'}),
  };

  const handleLogoUpload = async (file: File) => {
    setBusy(true);
    try {
      const result = await api.uploadLogo(userId, project.id, file);
      setLogoUrl(result.logo_url);
      update('logo_s3_key', result.s3_key);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'فشل رفع الشعار');
    } finally {
      setBusy(false);
    }
  };

  const handleApplyAndRender = async () => {
    setBusy(true);
    setError('');
    try {
      await api.saveRenderSettings(userId, project.id, settings);
      onReRender?.();
      await s.startRender();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'فشل الحفظ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{padding: 18}}>
      <div className="h-title" style={{marginBottom: 14}}>تخصيص الفيديو</div>

      {/* Live CSS Preview */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: ASPECT_CSS[project.aspect_ratio] ?? '9 / 16',
          background: bgUrl
            ? `url(${bgUrl}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1a1a2e, #16213e)',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 16,
          maxHeight: 260,
        }}
      >
        <div style={positionStyle}>
          <span style={textStyle}>{previewText}</span>
        </div>
        {(logoUrl || settings.logo_s3_key) && (
          <img
            src={logoUrl || (settings.logo_s3_key.startsWith('media/') ? `/${settings.logo_s3_key}` : '')}
            alt="logo"
            style={logoStyle}
          />
        )}
      </div>

      {/* Controls */}
      <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <div>
          <label style={{font: '600 12px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 4, display: 'block'}}>
            موضع النص
          </label>
          <SegControl options={TEXT_POSITIONS} value={settings.text_position} onChange={(v) => update('text_position', v)} />
        </div>

        <div>
          <label style={{font: '600 12px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 4, display: 'block'}}>
            حجم الخط
          </label>
          <SegControl options={FONT_SIZES} value={settings.font_size} onChange={(v) => update('font_size', v)} />
        </div>

        <div>
          <label style={{font: '600 12px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 4, display: 'block'}}>
            لون النص
          </label>
          <div style={{display: 'flex', gap: 8}}>
            {TEXT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => update('text_color', c.value)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: c.css,
                  border: settings.text_color === c.value ? '3px solid var(--accent, #4a9eff)' : '2px solid var(--rule)',
                  cursor: 'pointer',
                }}
                title={c.label}
              />
            ))}
          </div>
        </div>

        <div>
          <label style={{font: '600 12px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 4, display: 'block'}}>
            خلفية النص
          </label>
          <SegControl options={TEXT_BACKGROUNDS} value={settings.text_background} onChange={(v) => update('text_background', v)} />
        </div>

        <div>
          <label style={{font: '600 12px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 4, display: 'block'}}>
            الشعار
          </label>
          <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
            <button
              type="button"
              className="btn-out sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              {logoUrl || settings.logo_s3_key ? 'تغيير الشعار' : 'رفع شعار PNG'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/webp"
              style={{display: 'none'}}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleLogoUpload(file);
              }}
            />
            {(logoUrl || settings.logo_s3_key) && (
              <SegControl
                options={LOGO_POSITIONS}
                value={settings.logo_position}
                onChange={(v) => update('logo_position', v)}
              />
            )}
          </div>
        </div>
      </div>

      {error && <div className="err-note" style={{marginTop: 10}}>{error}</div>}

      <button
        type="button"
        className="btn lg"
        style={{marginTop: 16, width: '100%'}}
        disabled={busy}
        onClick={() => void handleApplyAndRender()}
      >
        {busy ? '⏳ جاري الإنتاج…' : '🎬 اعتمد وأنتج'}
      </button>
    </div>
  );
}

export function PreviewScreen() {
  const s = useStudio();
  const project = s.project!;
  const scenes = s.scenes();
  const {render} = s;

  const [previousUrl, setPreviousUrl] = useState<string>('');
  const [comparing, setComparing] = useState(false);

  const voice = scenes.map((sc) => sc.voiceover?.voice).find(Boolean);
  const lastFailed = [...project.document.renders].reverse().find((r) => r.status === 'failed');

  // This panel describes the exported file, so the video's own duration wins over the
  // storyboard's planned total — a scene dropped from the render makes them differ, and
  // the honest number is the one the viewer will actually watch.
  const planned = scenes.reduce((sum, sc) => sum + sc.duration_s, 0);
  const duration = render.durationS || planned;

  const details: [string, string, 'rtl' | 'ltr'][] = [
    ['النوع', 'نهائي (MP4)', 'rtl'],
    ['نسبة العرض', project.aspect_ratio, 'ltr'],
    ['الأبعاد', ASPECT_DIMENSIONS[project.aspect_ratio] ?? '—', 'ltr'],
    ['المدة', `${arNum(Math.round(duration))} ثانية`, 'rtl'],
    ['الحجم', render.sizeBytes === null ? '—' : fileSize(render.sizeBytes), 'ltr'],
    ['الصوت', voice ? `عربي · صوت ${voice}` : 'بلا تعليق صوتي', 'rtl'],
  ];

  return (
    <main className="preview-screen">
      <div className="pv-head">
        <button
          type="button"
          className="btn-out"
          style={{padding: '10px 18px', fontSize: 13}}
          onClick={() => s.go('workspace')}
        >
          <Forward />
          عودة إلى التحرير
        </button>
        <div style={{textAlign: 'center', minWidth: 0}}>
          <div className="t">{project.title}</div>
          <div className="d">
            {render.url ? 'النسخة النهائية' : 'لم تُصدَّر بعد'} · {formatWhen(project.updated_at)}
          </div>
        </div>
        <span className={`chip ${render.url ? 'ready' : 'draft'}`}>
          {render.url ? 'جاهز' : 'غير مُصدَّر'}
        </span>
      </div>

      <div className="pv-grid">
        <div className="pv-stage">
          {comparing && render.phase === 'running' && previousUrl ? (
            <>
              <div style={{textAlign: 'center', padding: 24}}>
                <span className="spin" style={{width: 28, height: 28}} />
                <p style={{font: '500 15px/2 var(--sans)', color: 'var(--ink)', marginTop: 14}}>جاري إنتاج نسخة جديدة بتخصيصاتك...</p>
              </div>
              <div className="panel" style={{padding: 14, marginTop: 14}}>
                <div style={{font: '500 13px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 8}}>النسخة الحالية (متاحة أثناء الإنتاج)</div>
                <VideoPlayer src={previousUrl} aspectRatio={ASPECT_CSS[project.aspect_ratio] ?? '9 / 16'} onDuration={() => {}} />
              </div>
            </>
          ) : comparing && render.phase === 'done' && previousUrl && render.url ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: 16, width: '100%'}}>
              <div className="panel" style={{padding: 14}}>
                <div style={{font: '600 14px/1.5 var(--sans)', color: 'var(--green, #2a7)', marginBottom: 10}}>✨ النسخة الجديدة</div>
                <VideoPlayer src={render.url} aspectRatio={ASPECT_CSS[project.aspect_ratio] ?? '9 / 16'} onDuration={s.setRenderDuration} />
                <div style={{display: 'flex', gap: 8, marginTop: 10, justifyContent: 'center'}}>
                  <a className="btn sm" href={render.url} download>قم بتحميل النسخة الجديدة</a>
                </div>
              </div>
              <div className="panel" style={{padding: 14}}>
                <div style={{font: '600 14px/1.5 var(--sans)', color: 'var(--muted)', marginBottom: 10}}>📁 النسخة السابقة</div>
                <VideoPlayer src={previousUrl} aspectRatio={ASPECT_CSS[project.aspect_ratio] ?? '9 / 16'} onDuration={() => {}} />
                <div style={{display: 'flex', gap: 8, marginTop: 10, justifyContent: 'center'}}>
                  <a className="btn-out sm" href={previousUrl} download>قم بتحميل النسخة السابقة</a>
                </div>
              </div>
              <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
                <button type="button" className="btn md" onClick={() => { setComparing(false); setPreviousUrl(''); }}>✓ اعتمد النسخة الجديدة</button>
                <button type="button" className="btn-out md" onClick={() => setComparing(false)}>↩ ارجع للنسخة السابقة</button>
              </div>
            </div>
          ) : render.url ? (
            <>
              <VideoPlayer
                src={render.url}
                aspectRatio={ASPECT_CSS[project.aspect_ratio] ?? '9 / 16'}
                onDuration={s.setRenderDuration}
              />
              <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center'}}>
                <a className="btn lg" href={render.url} download>
                  <Download size={15} />
                  قم بتحميل الفيديو
                </a>
                <PublishButton projectId={project.id} userId={s.user!.id} />
                <div className="seg" style={{background: 'var(--paper-raised)'}}>
                  {ASPECTS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={project.aspect_ratio === a ? 'on' : ''}
                      disabled={s.isBusy('aspect')}
                      onClick={() => s.setProjectAspect(a)}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="panel" style={{padding: '40px 26px', textAlign: 'center', width: '100%'}}>
              <b style={{font: '700 17px/1.8 var(--naskh)', color: 'var(--ink)'}}>
                لا توجد نسخة جاهزة بعد
              </b>
              <p className="empty-note" style={{margin: '6px 0 16px'}}>
                أنتج الفيديو من مساحة العمل لتظهر المعاينة هنا.
              </p>
              <button type="button" className="btn sm" onClick={() => s.go('workspace')}>
                عودة إلى التحرير
              </button>
              {lastFailed?.error && (
                <div className="err-note" style={{marginTop: 14}}>
                  {lastFailed.error}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: 18}}>
          <CustomizationPanel onReRender={() => {
            if (render.url) setPreviousUrl(render.url);
            setComparing(true);
          }} />

          <div className="panel" style={{padding: 18}}>
            <div className="h-title" style={{marginBottom: 12}}>
              تفاصيل النسخة
            </div>
            {details.map(([label, value, dir]) => (
              <div className="meta-row" key={label}>
                <span>{label}</span>
                <span dir={dir}>{value}</span>
              </div>
            ))}
          </div>

          <div className="panel" style={{padding: 18}}>
            <div className="h-title" style={{marginBottom: 12}}>
              المشاهد في هذه النسخة
            </div>
            {scenes.map((sc) => (
              <div className="sc-row" key={sc.id}>
                <div className="m">{arNum(sc.order)}</div>
                <div className="t">{sc.on_screen_copy || sc.script_text}</div>
                <span dir="ltr" className="mono" style={{fontSize: 11.5, color: 'var(--muted)'}}>
                  {sc.duration_s}s
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
